import React, { useEffect } from "react";
import AOS from "aos";
import { Link } from "react-router-dom";
import "aos/dist/aos.css";
import { BarChart2, FileText, Award, Monitor, Users } from "lucide-react";

// Curriculum
const curriculum = [
  {
    title: "Excel Basics & Navigation",
    img: "https://img.icons8.com/color/96/microsoft-excel-2019--v1.png",
    desc: "Get comfortable with Excel basics, navigation, shortcuts, and essential formatting.",
  },
  {
    title: "Formulas & Functions",
    img: "https://img.icons8.com/color/96/formula-fx.png",
    desc: "Master formulas like SUM, IF, VLOOKUP, INDEX-MATCH for advanced calculations.",
  },
  {
    title: "Data Formatting & Cleaning",
    img: "https://img.icons8.com/color/96/broom.png",
    desc: "Learn how to clean and format datasets for accurate analysis.",
  },
  {
    title: "Charts & Data Visualization",
    img: "https://img.icons8.com/color/96/combo-chart.png",
    desc: "Create visually appealing charts and graphs for business insights.",
  },
  {
    title: "Pivot Tables & Reports",
    img: "https://img.icons8.com/color/96/report-card.png", // new working image
    desc: "Summarize and analyze large datasets using Pivot Tables and dynamic reports.",
  },
  {
    title: "Conditional Formatting",
    img: "https://img.icons8.com/color/96/paint-bucket.png",
    desc: "Highlight trends, exceptions, and patterns in your data.",
  },
  {
    title: "Macros & Automation",
    img: "https://img.icons8.com/color/96/settings.png", // new working image
    desc: "Automate repetitive tasks using Macros and VBA scripts.",
  },
  {
    title: "Excel for Business & Finance",
    img: "https://img.icons8.com/color/96/banknotes.png",
    desc: "Apply Excel skills to real-world finance and business problems.",
  },
];

// Careers
const careers = [
  {
    title: "Data Analyst",
    desc: "Analyze business data, create reports, and provide actionable insights to support decision-making.",
  },
  {
    title: "Business Analyst",
    desc: "Use Excel to evaluate business processes, prepare reports, and recommend improvements.",
  },
  {
    title: "Financial Analyst",
    desc: "Manage financial data, generate statements, and perform analysis for budgeting and forecasting.",
  },
  {
    title: "MIS Executive",
    desc: "Prepare management information reports, dashboards, and summaries for operational and strategic decisions.",
  },
  {
    title: "Operations Manager",
    desc: "Leverage Excel skills to monitor business operations, streamline processes, and optimize performance.",
  },
];

// FAQs
const faqs = [
  {
    question: "Is this Excel course for beginners?",
    answer:
      "Yes, it covers Excel from scratch and also advanced features like Pivot Tables & Macros.",
  },
  {
    question: "Will I learn data visualization?",
    answer:
      "Definitely! We cover charts, graphs, and dashboards for business insights.",
  },
  {
    question: "Will I get real-world projects?",
    answer:
      "Yes, you’ll work on business case studies and Excel-based data projects.",
  },
  {
    question: "Do you provide certification?",
    answer:
      "Yes, you’ll receive an industry-recognized certificate upon completion.",
  },
];

// Enroll Benefits
const enrollBenefits = [
  { icon: "📊", text: "Master Excel from Basic to Advanced" },
  { icon: "💼", text: "Excel for Real-World Business Use" },
  { icon: "🏆", text: "Get Certified & Boost Your Career" },
];

const gradients = [
  "linear-gradient(135deg, #d1fae5, #a7f3d0)", // light green
  "linear-gradient(135deg, #e0f2fe, #bae6fd)", // light blue
  "linear-gradient(135deg, #fef9c3, #fef08a)", // light yellow
  "linear-gradient(135deg, #fee2e2, #fecaca)", // light red/pink
  "linear-gradient(135deg, #ede9fe, #ddd6fe)", // light purple
  "linear-gradient(135deg, #fff7ed, #ffedd5)", // light orange
  "linear-gradient(135deg, #f0fdfa, #ccfbf1)", // light teal
  "linear-gradient(135deg, #fef2f2, #fee2e2)", // light pink
];

const gradient = [
  "linear-gradient(135deg, #d1fae5, #a7f3d0)", //
  "linear-gradient(135deg, #e0f2fe, #bae6fd)", // light blue
  "linear-gradient(135deg, #fef9c3, #fef08a)", // light yellow
  "linear-gradient(135deg, #fee2e2, #fecaca)", // light pink
  "linear-gradient(135deg, #ede9fe, #ddd6fe)", // light purple ];
];
export default function Excel() {
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
        minHeight: "100vh",
      }}
    >
      {/* HERO */}
      <header
        style={{
          background:
            "url('/assets/f43f7602e095bff69deaf621703633ce.jpg')",
          color: "#fff",
          padding: "7rem 2rem", // increased from 3rem → 6rem
          textAlign: "center",
          borderRadius: "0 0 2rem 2rem",
          boxShadow: "0 4px 24px rgba(22,163,74,0.4)",
        }}
        data-aos="fade-down"
        className="mt-5"
      >
        <h1 style={{ fontSize: "3rem", fontWeight: 800 }}>MS Excel Course</h1>
        <p style={{ marginTop: "0.5rem" }} className="text-lg md:text-xl">
          Learn Excel like a Pro – From Basics to Business Dashboards
        </p>
        <Link to="/contact">
          <button
            style={{
              marginTop: "1.5rem",
              padding: "0.9rem 2rem",
              background: "#facc15",
              border: "none",
              borderRadius: "1rem",
              color: "#14532d",
              fontSize: "1.1rem",
              fontWeight: 700,
              cursor: "pointer",
              animation: "float 3s infinite",
            }}
          >
            Enroll Now 🚀
          </button>
        </Link>
      </header>

      <main style={{ maxWidth: 950, margin: "2rem auto", padding: "0 1rem" }}>
        {/* CURRICULUM */}
        <section>
          <h2
            style={{
              fontSize: "2rem",
              fontWeight: 700,
              textAlign: "center",
              marginBottom: "1.5rem",
              color: "#166534",
            }}
            data-aos="fade-up"
          >
            What You’ll Learn
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "1.5rem",
            }}
          >
            {curriculum.map((item, idx) => (
              <div
                key={item.title}
                data-aos="flip-left"
                data-aos-delay={idx * 120}
                className="pulse-animate"
                style={{
                  background: gradients[idx % gradients.length], // apply gradient
                  borderRadius: "1rem",
                  boxShadow: "0 2px 12px rgba(22,163,74,0.2)",
                  padding: "1.2rem",
                  textAlign: "center",
                }}
              >
                <img
                  src={item.img}
                  alt={item.title}
                  style={{ width: "3rem", marginBottom: "0.5rem" }}
                />
                <span
                  style={{
                    fontWeight: 600,
                    color: "#166534",
                    display: "block",
                    marginBottom: "0.5rem",
                  }}
                >
                  {item.title}
                </span>
                <p style={{ fontSize: "0.95rem", color: "#065f46" }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </section>
        {/* CAREERS */}

        <section className="py-20 mt-5" style={{ background: "#f0fdf4" }}>
          <div className="container mx-auto px-6">
            <h2
              style={{
                fontSize: "2rem",
                fontWeight: 700,
                textAlign: "center",
                marginBottom: "2rem",
                color: "#166534",
              }}
              data-aos="fade-up"
            >
              Career Opportunities
            </h2>

            <div
              className="grid gap-6"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              }}
            >
              {careers.map((career, idx) => {
                const iconMap = {
                  "Data Analyst": BarChart2,
                  "Business Analyst": FileText,
                  "Financial Analyst": Award,
                  "MIS Executive": Monitor,
                  "Operations Manager": Users,
                };
                const Icon = iconMap[career.title];

                return (
                  <div
                    key={career.title}
                    className="p-6 rounded-xl shadow-md transition-all transform hover:scale-105 hover:shadow-lg"
                    data-aos={idx % 2 === 0 ? "fade-up-right" : "fade-up-left"}
                    data-aos-delay={idx * 150}
                    style={{
                      background: gradient[idx % gradient.length],
                      borderRadius: "1rem",
                    }}
                  >
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full border-2 border-gray-700 text-gray-800">
                      <Icon size={28} />
                    </div>
                    <h3 className="font-semibold text-xl mb-2 text-center text-gray-800">
                      {career.title}
                    </h3>
                    <p className="text-gray-700 text-sm text-center">
                      {career.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
        {/* FAQ */}
        <section style={{ marginTop: "3rem" }}>
          <h2
            style={{
              fontSize: "2rem",
              fontWeight: 700,
              textAlign: "center",
              marginBottom: "1.5rem",
              color: "#166534",
            }}
            data-aos="fade-up"
          >
            FAQs
          </h2>
          <div style={{ display: "grid", gap: "1.2rem" }}>
            {faqs.map((faq, idx) => (
              <div
                key={faq.question}
                data-aos="fade-right"
                data-aos-delay={idx * 200}
                className="bounce-animate"
                style={{
                  background: "#fff",
                  borderRadius: "0.8rem",
                  padding: "1rem 1.2rem",
                  boxShadow: "0 2px 10px rgba(22,163,74,0.2)",
                }}
              >
                <strong style={{ color: "#22c55e" }}>{faq.question}</strong>
                <p style={{ marginTop: "0.5rem", color: "#166534" }}>
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </section>
        {/* ENROLL SECTION */}
        <section
          style={{
            marginTop: "4rem",
            padding: "3rem 1rem",
            background: "#166534",
            color: "#fff",
            borderRadius: "1.5rem",
            textAlign: "center",
            boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
          }}
          data-aos="zoom-in"
        >
          <h2
            style={{
              fontSize: "2rem",
              fontWeight: "bold",
              marginBottom: "1rem",
            }}
          >
            Why Choose Our Excel Course?
          </h2>
          <p
            style={{
              fontSize: "1.1rem",
              marginBottom: "2rem",
              color: "#bbf7d0",
            }}
          >
            Hands-on training, real business case studies, and industry-ready
            skills.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "1.5rem",
              marginBottom: "2rem",
            }}
          >
            {enrollBenefits.map((benefit, i) => (
              <div
                key={i}
                data-aos="fade-up"
                data-aos-delay={i * 150}
                className="card-animate"
                style={{
                  background: "#22c55e",
                  borderRadius: "1rem",
                  padding: "1rem",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                }}
              >
                <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>
                  {benefit.icon}
                </div>
                <p style={{ margin: 0, fontWeight: 600 }}>{benefit.text}</p>
              </div>
            ))}
          </div>

          <Link
            to="/contact"
            style={{
              padding: "1rem 2rem",
              background: "#facc15",
              borderRadius: "1rem",
              color: "#14532d",
              fontSize: "1.2rem",
              fontWeight: 700,
              textDecoration: "none",
              display: "inline-block",
              boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
              animation: "float 3s infinite",
            }}
          >
            Start Learning Excel Today 🚀
          </Link>
        </section>
      </main>

      {/* Animations */}
      <style jsx>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }
        .pulse-animate {
          animation: pulse 3s infinite;
        }
        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
          100% {
            transform: scale(1);
          }
        }
        .shake-animate {
          animation: shake 3s infinite;
        }
        @keyframes shake {
          0%,
          100% {
            transform: translate(0, 0);
          }
          25% {
            transform: translate(-2px, 2px);
          }
          50% {
            transform: translate(2px, -2px);
          }
          75% {
            transform: translate(-2px, 2px);
          }
        }
        .bounce-animate {
          animation: bounce 2.5s infinite;
        }
        @keyframes bounce {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-6px);
          }
        }
      `}</style>
    </div>
  );
}
